import { axiosCompanyPortalInstance } from "./";

/**
 * Company edit requests — company-portal owner proposes profile changes;
 * admin reviews & approves/rejects them.
 *
 * Base path (relative to the portal axios instance):
 *   /company_edit_requests
 *
 * Endpoints:
 *   POST    /company_edit_requests        -> create a new edit request (status: pending)
 *   GET     /company_edit_requests/:id    -> show a single edit request
 *   PATCH   /company_edit_requests/:id    -> update it (only while pending)
 *   DELETE  /company_edit_requests/:id    -> cancel it (only while pending)
 *
 * Responses are JSON:API shaped `{ data: { id, type, attributes } }` and some
 * failure cases come back as HTTP 200 with `{ success: false, message }`.
 * We flatten success payloads and raise on failure bodies so callers get a
 * consistent contract.
 */

const RESOURCE_PATH = "/company_edit_requests";

/** JSON:API single resource `{ id, type, attributes }` → flat object. */
const flattenResource = (node) => {
  if (!node || typeof node !== "object") return node;
  const attrs = node.attributes;
  if (attrs && typeof attrs === "object") {
    return {
      ...attrs,
      id: node.id ?? attrs.id,
      type: node.type,
    };
  }
  return node;
};

const pickData = (response) => {
  const raw = response?.data?.data ?? response?.data;
  return flattenResource(raw);
};

/** Some portal endpoints return HTTP 200 with `{ success: false }` — treat as failure. */
const throwIfBodyFailed = (response) => {
  const body = response?.data;
  if (!body || body.success !== false) return;
  let msg = body.message ?? body.error ?? "";
  if (Array.isArray(msg)) {
    msg = msg.filter(Boolean).join(", ");
  }
  if (typeof msg !== "string") {
    msg = String(msg || "");
  }
  const error = new Error(msg.trim() || "Request failed");
  error.response = { status: response.status, data: body };
  throw error;
};

/**
 * POST /company_edit_requests
 * `payload` must use backend field names:
 *   company_name_en, company_name_ar, company_city_id,
 *   company_image_url, company_commercial_record_url,
 *   company_owner_name, company_owner_phone_number, dial_code,
 *   company_owner_personal_id_url, company_owner_delegation_document_url
 * Country is resolved by the BE from `dial_code` — do NOT send country_id.
 */
export const createCompanyEditRequest = async (payload) => {
  const response = await axiosCompanyPortalInstance.post(RESOURCE_PATH, payload);
  throwIfBodyFailed(response);
  return pickData(response);
};

/** GET /company_edit_requests/:id — returns the edit request regardless of status. */
export const getCompanyEditRequest = async (id) => {
  const response = await axiosCompanyPortalInstance.get(
    `${RESOURCE_PATH}/${id}`,
  );
  throwIfBodyFailed(response);
  return pickData(response);
};

/**
 * PATCH /company_edit_requests/:id — partial update, only while status === "pending".
 * Any subset of the Create fields may be sent; omitted fields keep their value.
 */
export const updateCompanyEditRequest = async (id, payload) => {
  const response = await axiosCompanyPortalInstance.patch(
    `${RESOURCE_PATH}/${id}`,
    payload,
  );
  throwIfBodyFailed(response);
  return pickData(response);
};

/** DELETE /company_edit_requests/:id — cancels the proposal, only while pending. */
export const deleteCompanyEditRequest = async (id) => {
  const response = await axiosCompanyPortalInstance.delete(
    `${RESOURCE_PATH}/${id}`,
  );
  throwIfBodyFailed(response);
  return response?.data ?? null;
};
